import * as React from 'react';

export type Route =
  'home' | 'applications' | 'clients' | 'library' | 'history' | 'settings' | 'review' | 'connect' | 'setup';

const ROUTES: Route[] = ['home', 'applications', 'clients', 'library', 'history', 'settings', 'review', 'connect', 'setup'];

function current(): Route {
  const r: string = window.location.hash.replace(/^#\/?/, '').split('/')[0];
  return (ROUTES.indexOf(r as Route) !== -1 ? r : 'home') as Route;
}

/** Screens are addressed by the page hash (#/applications), so Back and bookmarks work. */
export function useRoute(): [Route, (r: Route) => void] {
  const [route, setRoute] = React.useState<Route>(current);
  React.useEffect(() => {
    const onChange = (): void => setRoute(current());
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  const go = React.useCallback((r: Route) => { window.location.hash = `/${r}`; }, []);
  return [route, go];
}

/** The parts of the address after the screen: #/applications/abc → ['abc']; query (?a=b) separately. */
export function hashParts(): { parts: string[]; query: URLSearchParams } {
  const raw: string = window.location.hash.replace(/^#\/?/, '');
  const [path, query] = raw.split('?');
  return { parts: path.split('/').slice(1).filter(p => !!p).map(decodeURIComponent), query: new URLSearchParams(query ?? '') };
}

/** Re-reads the address parts whenever it changes. */
export function useHashParts(): { parts: string[]; query: URLSearchParams } {
  const [value, setValue] = React.useState(hashParts);
  React.useEffect(() => {
    const onChange = (): void => setValue(hashParts());
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return value;
}
